import React from 'react';
import { AxiosError } from 'axios';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import {
  fetchDocument,
  fetchDocumentPath,
  FetchDocumentType,
  fetchPosterAuthors,
  getDownloadPosterUrl,
} from 'services/documents';
import { fetchPosterKeywords } from 'api/poster';
import { fetchCommonEventData } from 'api/events';
import Article from 'components/Article/index';
import { handleAxiosError, IError } from 'utils/handleAxiosError';
import {
  EventStandardContainer,
  EventStandardContainerData,
} from 'components/EventStandardContainer';
import 'video.js/dist/video-js.css';
import { logger } from 'utils/logger';
import { RtmpProvider } from 'containers/SingleSession/Rtmp/RtmpProvider';
import { fetchVideoSession } from 'api/session';

export type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const ArticlePage: NextPage<PageProps> = ({ data, error, metaTags }) => (
  <RtmpProvider streamType="MORRESSIER_STREAM">
    <EventStandardContainer
      data={data}
      error={error}
      metaTags={metaTags}
      Component={Article}
      showIfEmbargoed
    />
  </RtmpProvider>
);

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});

const resolveArticlePath = async (article_id: string, is_embargoed: boolean) => {
  if (is_embargoed) return null;
  const resp = await fetchDocumentPath(article_id);

  const { organization_id = 'no_defined', event_id, document_id } = resp;
  return { organization_id, event_id, document_id, article_id };
};

export const getStaticProps: GetStaticProps<
  {
    data?: EventStandardContainerData<{
      article_id: string;
      event_id: string;
      organization_id?: string;
      document_id?: string;
      document: FetchDocumentType | null;
      videoSession: MorressierVideoSession | null;
      data: { processing_state: 2; articleKeywords };
    }>;
    metaTags?: Record<string, string>;
    error?: IError;
  },
  { eventId: string; article_id: string }
> = async ctx => {
  const eventId = ctx.params?.eventId as string;
  const article_id = ctx.params?.article_id as string;

  try {
    const [{ eventInfo, tabsConfig }, articleKeywords, videoSession] = await Promise.all([
      fetchCommonEventData(eventId),
      fetchPosterKeywords(eventId, article_id, true),
      fetchVideoSession(article_id, 'poster'),
    ]);

    const articleQuery = await resolveArticlePath(article_id, eventInfo.event.embargoed);
    let document: ResolvedPromise<ReturnType<typeof fetchDocument>> | null = null;

    if (articleQuery) {
      document = await fetchDocument(articleQuery);
      if (document) {
        const posterUrl = await getDownloadPosterUrl(document.id).catch(() =>
          logger.log('error getting download poster url'),
        );

        // hack for event_manager returning not existing pdf url
        // for documents missing pdf
        if (!posterUrl?.includes('None.pdf')) {
          document.pdfUrl = posterUrl ?? '';
        }

        // @ts-ignore authors need to be MorressierUser here: need to revisit this type

        document.authors = await fetchPosterAuthors(document.id);
        document.keywords = articleKeywords;
      }
    }
    const metaTags = {
      title: document?.title || '',
      description: `${(document?.paper_abstract || document?.title || '').slice(0, 145)} | ${
        document?.type || ''
      }`,
      citation_doi: document?.doi || '',
    };
    return {
      props: {
        data: {
          article_id,
          event_id: eventId,
          ...articleQuery,
          ...eventInfo,
          tabsConfig,
          document,
          videoSession,
          data: { processing_state: 2, articleKeywords },
        },
        metaTags,
      },
      revalidate: 60,
    };
  } catch (err) {
    const error = handleAxiosError(err as AxiosError);

    return {
      props: { error },
      revalidate: 60,
    };
  }
};

export default ArticlePage;
